import React, { useState, useCallback, useEffect } from 'react';

const PixRenkeiModal = ({ 
  isOpen, 
  onClose, 
  onApprove, 
  onReject, 
  isProcessing = false, // 処理中フラグ
  companyName = '' // 会社名
}) => {
  // 重複クリック防止用の状態
  const [isClicked, setIsClicked] = useState(false);

  // モーダルが閉じられた時に状態をリセット
  useEffect(() => {
    if (!isOpen) {
      setIsClicked(false);
    }
  }, [isOpen]);

  // 承認ボタンクリック処理（重複クリック防止）
  const handleApprove = useCallback(() => {
    setIsClicked(prevClicked => {
      if (isProcessing || prevClicked) return prevClicked;
      onApprove?.();
      return true;
    });
  }, [isProcessing, onApprove]);

  // 拒否ボタンクリック処理（重複クリック防止）
  const handleReject = useCallback(() => {
    setIsClicked(prevClicked => {
      if (isProcessing || prevClicked) return prevClicked;
      onReject?.();
      return true;
    });
  }, [isProcessing, onReject]);

  // 閉じるボタンクリック処理
  const handleClose = useCallback(() => {
    if (isProcessing) return;
    onClose?.();
  }, [isProcessing, onClose]);

  // モーダルが閉じている場合は何も表示しない
  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* ヘッダー */}
        <div style={styles.header}>
          <h3 style={styles.title}>招待承認</h3>
        </div>
        
        {/* コンテンツ */}
        <div style={styles.content}>
          {companyName && (
            <p style={styles.companyName}>
              {companyName}
            </p>
          )}
          <p style={styles.message}>
            からユーザー追加の招待がきています。
          </p>
          <p style={styles.description}>
            承認後は、pix-labelの再ログインが必要です。
          </p>
        </div>
        
        {/* アクションボタン */}
        <div style={styles.actions}>
          <button
            style={isProcessing || isClicked ? styles.rejectButtonDisabled : styles.rejectButton}
            onClick={handleReject}
            disabled={isProcessing || isClicked}
          >
            承認しない
          </button>
          <button
            style={isProcessing || isClicked ? styles.approveButtonDisabled : styles.approveButton}
            onClick={handleApprove}
            disabled={isProcessing || isClicked}
          >
            承認する
          </button>
        </div>
        
        {/* 閉じるボタン */}
        <button
          style={styles.closeButton}
          onClick={handleClose}
          disabled={isProcessing}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// CSS-in-JS スタイル定義
const styles = {
  // オーバーレイ（背景）
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  // モーダル本体
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    maxWidth: '400px',
    width: '90%',
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  // ヘッダー
  header: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  // タイトル
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  // コンテンツ
  content: {
    marginBottom: '25px'
  },
  // 会社名表示
  companyName: {
    fontSize: '18px',
    margin: '0 0 15px 0',
    textAlign: 'center',
    color: '#2B2222',
    fontWeight: 'bold',
    padding: '10px',
    background: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #dee2e6'
  },
  // メッセージ
  message: {
    fontSize: '16px',
    margin: '0 0 10px 0',
    textAlign: 'center',
    color: '#333'
  },
  // 説明文
  description: {
    fontSize: '14px',
    margin: 0,
    textAlign: 'center',
    color: '#666',
    lineHeight: '1.4'
  },
  // アクションボタンエリア
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  // 承認ボタン
  approveButton: {
    padding: '12px 24px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  // 承認ボタン（無効状態）
  approveButtonDisabled: {
    padding: '12px 24px',
    backgroundColor: '#cccccc',
    color: '#666666',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'not-allowed',
    transition: 'background-color 0.3s ease'
  },
  // 拒否ボタン
  rejectButton: {
    padding: '12px 24px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  // 拒否ボタン（無効状態）
  rejectButtonDisabled: {
    padding: '12px 24px',
    backgroundColor: '#cccccc',
    color: '#666666',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'not-allowed',
    transition: 'background-color 0.3s ease'
  },
  // 閉じるボタン
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#999',
    padding: '5px'
  }
};

export default PixRenkeiModal;
